import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaSearch, FaFilter, FaCloudDownloadAlt, FaCloudUploadAlt, FaFileExcel, FaSortAmountDown, FaSortAmountUp, FaListOl } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { transferPricing } from '../../services/api';
import debounce from 'lodash/debounce';
import * as XLSX from 'xlsx';

const TransferePage = () => {
  const { t } = useTranslation();
  const [pricings, setPricings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPricings, setSelectedPricings] = useState([]);
  const [showBulkEditForm, setShowBulkEditForm] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    lowSeasonPrice: '',
    mediumSeasonPrice: '',
    highSeasonPrice: '',
    carType: ''
  });
  const [newPricing, setNewPricing] = useState({
    fromLocation: '',
    toLocation: '',
    lowSeasonPrice: 0,
    mediumSeasonPrice: 0,
    highSeasonPrice: 0,
    carType: 'car'
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [sortField, setSortField] = useState('fromLocation');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isDeleting, setIsDeleting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    fromLocation: '',
    toLocation: '',
    minPrice: '',
    maxPrice: '',
    carType: ''
  });
  const [editingPricing, setEditingPricing] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showEditSelectedForm, setShowEditSelectedForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fileInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const formRef = useRef(null);

  // Fetch all transfer pricings
  const fetchPricings = async () => {
    setLoading(true);
    try {
      const response = await transferPricing.getAll('/transfer-pricing');
      if (!response.status === 200) {
        throw new Error('Failed to fetch pricing data');
      }

      const data = response.data;
      setPricings(data);

      // Extract unique locations for filter dropdowns
      const allLocations = new Set();
      data.forEach(pricing => {
        allLocations.add(pricing.fromLocation);
        allLocations.add(pricing.toLocation);
      });
      setLocations(Array.from(allLocations).sort());

    } catch (error) {
      console.error('Error fetching pricing data:', error);
      toast.error(t(error.message || 'Error fetching pricing data'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricings();

    // Set up keyboard shortcuts
    const handleKeyDown = (e) => {
      // Ctrl+F for search focus
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }

      // Escape to cancel editing
      if (e.key === 'Escape') {
        setEditingPricing(null);
        setShowAddForm(false);
      }

      // Ctrl+N for new pricing
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        setShowAddForm(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Array of available car types for selection
  const carTypes = ['car', 'minivan', 'van', 'minibus'];

  // Apply sorting and filtering
  const filteredAndSortedPricings = pricings
    .filter(pricing => {
      const matchesSearch = searchTerm === '' ||
        pricing.fromLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pricing.toLocation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (pricing.carType && pricing.carType.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesFromFilter = !filterOptions.fromLocation ||
        pricing.fromLocation === filterOptions.fromLocation;

      const matchesToFilter = !filterOptions.toLocation ||
        pricing.toLocation === filterOptions.toLocation;

      const matchesCarType = !filterOptions.carType ||
        pricing.carType === filterOptions.carType;

      const matchesMinPrice = !filterOptions.minPrice ||
        Math.min(pricing.lowSeasonPrice, pricing.mediumSeasonPrice, pricing.highSeasonPrice) >=
        Number(filterOptions.minPrice);

      const matchesMaxPrice = !filterOptions.maxPrice ||
        Math.max(pricing.lowSeasonPrice, pricing.mediumSeasonPrice, pricing.highSeasonPrice) <=
        Number(filterOptions.maxPrice);

      return matchesSearch && matchesFromFilter && matchesToFilter && matchesMinPrice && matchesMaxPrice && matchesCarType;
    })
    .sort((a, b) => {
      let comparison = 0;

      if (sortField === 'fromLocation') {
        comparison = a.fromLocation.localeCompare(b.fromLocation);
      } else if (sortField === 'toLocation') {
        comparison = a.toLocation.localeCompare(b.toLocation);
      } else if (sortField === 'carType') {
        comparison = (a.carType || '').localeCompare(b.carType || '');
      } else if (['lowSeasonPrice', 'mediumSeasonPrice', 'highSeasonPrice'].includes(sortField)) {
        comparison = a[sortField] - b[sortField];
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

  // Get paginated data
  const paginatedPricings = filteredAndSortedPricings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredAndSortedPricings.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Select all visible pricings
  const handleSelectAll = () => {
    if (selectedPricings.length === paginatedPricings.length) {
      setSelectedPricings([]);
    } else {
      setSelectedPricings(paginatedPricings.map(p => p._id));
    }
  };

  // Toggle selection of individual pricing
  const handleSelectPricing = (id) => {
    setSelectedPricings(prev =>
      prev.includes(id)
        ? prev.filter(p => p !== id)
        : [...prev, id]
    );
  };

  // Add new pricing
  const handleAddPricing = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://rent-server.create-site.gr/api/transfer-pricing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newPricing)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create pricing');
      }

      const data = await response.json();
      setPricings(prev => [...prev, data]);
      setNewPricing({
        fromLocation: '',
        toLocation: '',
        lowSeasonPrice: 0,
        mediumSeasonPrice: 0,
        highSeasonPrice: 0
      });
      setShowAddForm(false);
      toast.success(t('Pricing added successfully'));
    } catch (error) {
      console.error('Error adding pricing:', error);
      toast.error(error.message || t('Error adding pricing'));
    }
  };

  // Start editing pricing
  const handleEditStart = (pricing) => {
    setEditingPricing({ ...pricing });
  };

  // Handle edit changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditingPricing(prev => ({
      ...prev,
      [name]: ['lowSeasonPrice', 'mediumSeasonPrice', 'highSeasonPrice'].includes(name)
        ? Number(value)
        : value
    }));
  };

  // Save edited pricing
  const handleEditSave = async () => {
    try {
      const response = await fetch(`https://rent-server.create-site.gr/api/transfer-pricing/${editingPricing._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingPricing)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update pricing');
      }

      setPricings(prev =>
        prev.map(p =>
          p._id === editingPricing._id ? editingPricing : p
        )
      );
      setEditingPricing(null);
      toast.success(t('Pricing updated successfully'));
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error(error.message || t('Error updating pricing'));
    }
  };

  // Delete pricing
  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this pricing?'))) return;

    setIsDeleting(true);
    try {
      const response = await fetch(`https://rent-server.create-site.gr/api/transfer-pricing/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Failed to delete pricing');

      setPricings(prev => prev.filter(p => p._id !== id));
      setSelectedPricings(prev => prev.filter(p => p !== id));
      toast.success(t('Pricing deleted successfully'));
    } catch (error) {
      console.error('Error deleting pricing:', error);
      toast.error(t('Error deleting pricing'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedPricings.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedPricings.length} pricing items?`))
      return;

    setIsDeleting(true);
    try {
      const response = await fetch('https://rent-server.create-site.gr/api/transfer-pricing/bulk-delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ids: selectedPricings })
      });

      if (!response.ok) throw new Error('Failed to delete selected pricings');

      setPricings(prev => prev.filter(p => !selectedPricings.includes(p._id)));
      setSelectedPricings([]);
      toast.success(t('Selected pricings deleted successfully'));
    } catch (error) {
      console.error('Error bulk deleting pricings:', error);
      toast.error(t('Error deleting selected pricings'));
    } finally {
      setIsDeleting(false);
    }
  };

  // Export to Excel
  const handleExport = () => {
    try {
      const dataToExport = filteredAndSortedPricings.map(pricing => ({
        'From Location': pricing.fromLocation,
        'To Location': pricing.toLocation,
        'Low Season Price': pricing.lowSeasonPrice,
        'Medium Season Price': pricing.mediumSeasonPrice,
        'High Season Price': pricing.highSeasonPrice,
        'Car Type': pricing.carType,
      }));

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transfer Pricing');

      // Set column widths
      const columnWidths = [
        { wch: 25 }, // From Location
        { wch: 25 }, // To Location
        { wch: 15 }, // Low Season
        { wch: 15 }, // Medium Season
        { wch: 15 },  // High Season
        { wch: 15 }  // Car Type
      ];
      worksheet['!cols'] = columnWidths;

      // Generate file name with date
      const date = new Date().toISOString().split('T')[0];
      const fileName = `transfer-pricing-${date}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      toast.success(t('Export successful'));
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(t('Error exporting data'));
    }
  };

  // Import from Excel
  const handleImportClick = () => {
    // log the process
    
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            toast.error(t('No data found in the imported file'));
            return;
          }

          // Validate and transform data
          const formattedData = jsonData.map(row => ({
            fromLocation: row['From Location'] || row['fromLocation'] || '',
            toLocation: row['To Location'] || row['toLocation'] || '',
            lowSeasonPrice: Number(row['Low Season Price'] || row['lowSeasonPrice'] || 0),
            mediumSeasonPrice: Number(row['Medium Season Price'] || row['mediumSeasonPrice'] || 0),
            highSeasonPrice: Number(row['High Season Price'] || row['highSeasonPrice'] || 0),
            carType: row['Car Type'] || row['carType'] || ''
          }));

          // Submit import data
          const response = await fetch('https://rent-server.create-site.gr/api/transfer-pricing/bulk-import', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ pricings: formattedData })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Import failed');
          }

          fetchPricings();
          toast.success(t('Import successful'));
        } catch (error) {
          console.error('Error processing import file:', error);
          toast.error(error.message || t('Error processing import file'));
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error importing data:', error);
      toast.error(t('Error importing data'));
    } finally {
      // Reset file input
      e.target.value = null;
    }
  };

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterOptions(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilterOptions({
      fromLocation: '',
      toLocation: '',
      minPrice: '',
      maxPrice: '',
      carType: ''
    });
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const getButtonClassName = (isActive = false) => `
    px-3 py-2 rounded-md font-medium transition-colors duration-150
    ${isActive
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
  `;


  const handleBulkEditChange = (e) => {
    const { name, value } = e.target;
    setBulkEditData(prev => ({
      ...prev,
      [name]: value
    }));
  }

  const handleBulkEditSave = async () => {
    try {
      const response = await transferPricing.bulkUpdate(selectedPricings, bulkEditData);
      if (!response.status === 200) {
        throw new Error('Failed to bulk edit pricing data');
      }
      await fetchPricings(); // Fetch fresh data from the server
      setSelectedPricings([]); // Clear selection after successful update
      setSelectedPricings([]);
      setShowBulkEditForm(false);
      toast.success(t('Bulk edit successful'));
    }
    catch (error) {
      console.error('Error bulk editing pricing data:', error);
      toast.error(t(error.message || 'Error bulk editing pricing data'));
    }
  }


  const handleBulkEdit = () => {
    if (selectedPricings.length === 0) {
      toast.error(t('Please select at least one pricing to edit'));
      return;
    }
    if (showBulkEditForm) {
      handleBulkEditSave();
    } else {
      setBulkEditData({
        lowSeasonPrice: '',
        mediumSeasonPrice: '',
        highSeasonPrice: '',
        carType: ''
      });
      setShowBulkEditForm(true);
    }

    setShowBulkEditForm(!showBulkEditForm);
  }


  // Add state for bulk add form
  const [showBulkAddForm, setShowBulkAddForm] = useState(false);
  const [bulkAddData, setBulkAddData] = useState({
    fromLocations: "",
    toLocations: "",
    lowSeasonPrice: 0,
    mediumSeasonPrice: 0,
    highSeasonPrice: 0,
    carType: "car"
  });

  // Handle bulk add change
  const handleBulkAddChange = (e) => {
    const { name, value } = e.target;
    setBulkAddData(prev => ({
      ...prev,
      [name]: ['lowSeasonPrice', 'mediumSeasonPrice', 'highSeasonPrice'].includes(name)
        ? Number(value)
        : value
    }));
  };

  // Handle bulk add submission
  const handleBulkAddSubmit = async (e) => {
    e.preventDefault();

    // Parse comma-separated locations
    const fromLocations = bulkAddData.fromLocations.split(',').map(loc => loc.trim()).filter(loc => loc);
    const toLocations = bulkAddData.toLocations.split(',').map(loc => loc.trim()).filter(loc => loc);

    if (fromLocations.length === 0 || toLocations.length === 0) {
      toast.error(t('Please enter at least one location for both From and To fields'));
      return;
    }

    try {
      setLoading(true);

      // Create all combinations of fromLocations and toLocations
      const pricings = [];
      for (const fromLocation of fromLocations) {
        for (const toLocation of toLocations) {
          // Skip if from and to are the same
          if (fromLocation === toLocation) continue;

          pricings.push({
            fromLocation,
            toLocation,
            lowSeasonPrice: bulkAddData.lowSeasonPrice,
            mediumSeasonPrice: bulkAddData.mediumSeasonPrice,
            highSeasonPrice: bulkAddData.highSeasonPrice,
            carType: bulkAddData.carType
          });
        }
      }

      if (pricings.length === 0) {
        toast.error(t('No valid location combinations were created'));
        setLoading(false);
        return;
      }

      const response = await fetch('https://rent-server.create-site.gr/api/transfer-pricing/bulk-import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pricings })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to bulk add pricing data');
      }

      // Reset form and fetch updated data
      setBulkAddData({
        fromLocations: "",
        toLocations: "",
        lowSeasonPrice: 0,
        mediumSeasonPrice: 0,
        highSeasonPrice: 0,
        carType: "car"
      });
      setShowBulkAddForm(false);
      await fetchPricings();
      toast.success(t(`Successfully added ${pricings.length} pricing routes`));
    } catch (error) {
      console.error('Error adding bulk pricing:', error);
      toast.error(error.message || t('Error adding bulk pricing'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">

      {/* Stats summary */}
      {pricings.length > 0 && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <h3 className="text-sm font-medium text-blue-800">{t('Total Routes')}</h3>
            <p className="text-2xl font-bold text-blue-900">{pricings.length}</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-100">
            <h3 className="text-sm font-medium text-green-800">{t('Total Destinations')}</h3>
            <p className="text-2xl font-bold text-green-900">{locations.length}</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
            <h3 className="text-sm font-medium text-purple-800">{t('Avg. Season Price')}</h3>
            <p className="text-2xl font-bold text-purple-900">
              {pricings.length > 0
                ? (
                  (pricings.reduce((sum, pricing) => sum + (pricing.lowSeasonPrice + pricing.mediumSeasonPrice + pricing.highSeasonPrice) / 3, 0) / pricings.length).toFixed(2)
                )
                : '0.00'
              }
            </p>
          </div>
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        <div className="flex-1 min-w-[280px]">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder={t('Search locations...')}
              className="pl-10 w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              onChange={handleSearchChange}
            />
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className={getButtonClassName(showFilters)}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter className="inline mr-1" /> {t('Filters')}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className={getButtonClassName()}
          onClick={handleImportClick}
        >
          <FaCloudUploadAlt className="inline mr-1" /> {t('Import')}
        </motion.button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .xls"
          onChange={handleImportFile}
          className="hidden"
        />

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className={getButtonClassName()}
          onClick={handleExport}
          disabled={pricings.length === 0}
        >
          <FaCloudDownloadAlt className="inline mr-1" /> {t('Export')}
        </motion.button>

        {selectedPricings.length > 0 && (
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            whileTap={{ scale: 0.97 }}
            className="px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            onClick={handleBulkDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <LoadingSpinner size="sm" />
            ) : (
              <>
                <FaTrash className="inline mr-1" />
                {t('Delete Selected')} ({selectedPricings.length})
              </>
            )}
          </motion.button>
        )}

        {/* edit selected */}
        {selectedPricings.length > 0 && (
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            whileTap={{ scale: 0.97 }}
            className="px-3 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
            onClick={handleBulkEdit}
          >
            <FaEdit className="inline mr-1" />
            {t('Edit Selected')} ({selectedPricings.length})
          </motion.button>
        )}

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className={getButtonClassName(showAddForm)}
          onClick={() => {
            setShowAddForm(!showAddForm);
            if (showBulkAddForm) setShowBulkAddForm(false);
          }}
        >
          {showAddForm ? (
            <><FaTimes className="inline mr-1" /> {t('Cancel')}</>
          ) : (
            <><FaPlus className="inline mr-1" /> {t('Add New')}</>
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className={getButtonClassName(showBulkAddForm)}
          onClick={() => {
            setShowBulkAddForm(!showBulkAddForm);
            if (showAddForm) setShowAddForm(false);
          }}
        >
          {showBulkAddForm ? (
            <><FaTimes className="inline mr-1" /> {t('Cancel')}</>
          ) : (
            <><FaListOl className="inline mr-1" /> {t('Bulk Add')}</>
          )}
        </motion.button>
      </div>

      {/* Filter panel */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 overflow-hidden"
          >
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('From Location')}</label>
                  <select
                    name="fromLocation"
                    value={filterOptions.fromLocation}
                    onChange={handleFilterChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('All')}</option>
                    {locations.map(location => (
                      <option key={location} value={location}>{location}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('To Location')}</label>
                  <select
                    name="toLocation"
                    value={filterOptions.toLocation}
                    onChange={handleFilterChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('All')}</option>
                    {locations.map(location => (
                      <option key={location} value={location}>{location}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Car Type')}</label>
                  <select
                    name="carType"
                    value={filterOptions.carType}
                    onChange={handleFilterChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('All')}</option>
                    {carTypes.map(type => (
                      <option key={type} value={type}>{t(type.charAt(0).toUpperCase() + type.slice(1))}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Min Price')}</label>
                  <input
                    type="number"
                    name="minPrice"
                    value={filterOptions.minPrice}
                    onChange={handleFilterChange}
                    placeholder={t('Min')}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Max Price')}</label>
                  <input
                    type="number"
                    name="maxPrice"
                    value={filterOptions.maxPrice}
                    onChange={handleFilterChange}
                    placeholder={t('Max')}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  {t('Clear Filters')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add new pricing form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-6"
          >
            <form
              ref={formRef}
              onSubmit={handleAddPricing}
              className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('From Location')}</label>
                  <input
                    type="text"
                    name="fromLocation"
                    value={newPricing.fromLocation}
                    onChange={(e) => setNewPricing({ ...newPricing, fromLocation: e.target.value })}
                    placeholder={t('From')}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    list="locationsList"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('To Location')}</label>
                  <input
                    type="text"
                    name="toLocation"
                    value={newPricing.toLocation}
                    onChange={(e) => setNewPricing({ ...newPricing, toLocation: e.target.value })}
                    placeholder={t('To')}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    list="locationsList"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Car Type')}</label>
                  <select
                    name="carType"
                    value={newPricing.carType}
                    onChange={(e) => setNewPricing({ ...newPricing, carType: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    {carTypes.map(type => (
                      <option key={type} value={type}>{t(type.charAt(0).toUpperCase() + type.slice(1))}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Low Season Price')}</label>
                  <input
                    type="number"
                    name="lowSeasonPrice"
                    value={newPricing.lowSeasonPrice}
                    onChange={(e) => setNewPricing({ ...newPricing, lowSeasonPrice: Number(e.target.value) })}
                    min="0"
                    step="0.01"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Medium Season Price')}</label>
                  <input
                    type="number"
                    name="mediumSeasonPrice"
                    value={newPricing.mediumSeasonPrice}
                    onChange={(e) => setNewPricing({ ...newPricing, mediumSeasonPrice: Number(e.target.value) })}
                    min="0"
                    step="0.01"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('High Season Price')}</label>
                  <input
                    type="number"
                    name="highSeasonPrice"
                    value={newPricing.highSeasonPrice}
                    onChange={(e) => setNewPricing({ ...newPricing, highSeasonPrice: Number(e.target.value) })}
                    min="0"
                    step="0.01"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end space-x-2">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  {t('Cancel')}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  {t('Add Pricing')}
                </motion.button>
              </div>

              <datalist id="locationsList">
                {locations.map(location => (
                  <option key={location} value={location} />
                ))}
              </datalist>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Add Form */}
      <AnimatePresence>
        {showBulkAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-6"
          >
            <form
              onSubmit={handleBulkAddSubmit}
              className="p-4 bg-green-50 border border-green-200 rounded-lg"
            >
              <h3 className="text-lg font-medium text-green-800 mb-4">{t('Bulk Add Transfer Pricing')}</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('From Locations')} <span className="text-xs text-gray-500">({t('comma separated')})</span>
                  </label>
                  <textarea
                    name="fromLocations"
                    value={bulkAddData.fromLocations}
                    onChange={handleBulkAddChange}
                    placeholder={t('Athens, Piraeus, Rafina...')}
                    rows={3}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('Enter multiple locations separated by commas')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('To Locations')} <span className="text-xs text-gray-500">({t('comma separated')})</span>
                  </label>
                  <textarea
                    name="toLocations"
                    value={bulkAddData.toLocations}
                    onChange={handleBulkAddChange}
                    placeholder={t('Glyfada, Vouliagmeni, Varkiza...')}
                    rows={3}
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('This will create routes from each "From" location to each "To" location with the specified pricing.')}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Car Type')}
                  </label>
                  <select
                    name="carType"
                    value={bulkAddData.carType}
                    onChange={handleBulkAddChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    {carTypes.map(type => (
                      <option key={type} value={type}>
                        {t(type.charAt(0).toUpperCase() + type.slice(1))}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Low Season Price')}
                  </label>
                  <input
                    type="number"
                    name="lowSeasonPrice"
                    value={bulkAddData.lowSeasonPrice}
                    onChange={handleBulkAddChange}
                    min="0"
                    step="0.01"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Medium Season Price')}
                  </label>
                  <input
                    type="number"
                    name="mediumSeasonPrice"
                    value={bulkAddData.mediumSeasonPrice}
                    onChange={handleBulkAddChange}
                    min="0"
                    step="0.01"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('High Season Price')}
                  </label>
                  <input
                    type="number"
                    name="highSeasonPrice"
                    value={bulkAddData.highSeasonPrice}
                    onChange={handleBulkAddChange}
                    min="0"
                    step="0.01"
                    required
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      {t('This will create multiple pricing routes at once. Each "From" location will be paired with each "To" location with the specified pricing.')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="button"
                  onClick={() => setShowBulkAddForm(false)}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  {t('Cancel')}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  disabled={loading}
                >
                  {loading ? <LoadingSpinner size="sm" /> : (
                    <>{t('Add Bulk Pricing')}</>
                  )}
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Edit Modal */}
      {showBulkEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{t('Bulk Edit Pricing')}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('Car Type')}
                </label>
                <select
                  name="carType"
                  value={bulkEditData.carType}
                  onChange={handleBulkEditChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">{t('Leave unchanged')}</option>
                  {carTypes.map(type => (
                    <option key={type} value={type}>{t(type.charAt(0).toUpperCase() + type.slice(1))}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('Low Season Price')}
                </label>
                <input
                  type="number"
                  name="lowSeasonPrice"
                  value={bulkEditData.lowSeasonPrice}
                  onChange={handleBulkEditChange}
                  placeholder={t('Leave empty to keep existing')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('Medium Season Price')}
                </label>
                <input
                  type="number"
                  name="mediumSeasonPrice"
                  value={bulkEditData.mediumSeasonPrice}
                  onChange={handleBulkEditChange}
                  placeholder={t('Leave empty to keep existing')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('High Season Price')}
                </label>
                <input
                  type="number"
                  name="highSeasonPrice"
                  value={bulkEditData.highSeasonPrice}
                  onChange={handleBulkEditChange}
                  placeholder={t('Leave empty to keep existing')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkEditForm(false);
                  setBulkEditData({
                    lowSeasonPrice: '',
                    mediumSeasonPrice: '',
                    highSeasonPrice: '',
                    carType: ''
                  });
                }}
                className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleBulkEdit}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
              >
                {t('Update')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : paginatedPricings.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <p className="text-lg text-gray-600 mb-4">{t('No transfer pricing data found')}</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <FaPlus className="inline mr-1" /> {t('Add Your First Pricing')}
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 md:-mx-6 rounded-lg">
          <div className="inline-block min-w-full px-4 md:px-6">
            <table className="min-w-full divide-y divide-gray-200 rounded-lg">
              <thead className="bg-gray-50 rounded-t-lg border">
                <tr>
                  <th scope="col" className="py-3 px-4 text-left">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        checked={selectedPricings.length === paginatedPricings.length && paginatedPricings.length > 0}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  <th scope="col" className="py-3 px-4 text-left">
                    <button
                      className="group flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
                      onClick={() => handleSort('fromLocation')}
                    >
                      <span>{t('From Location')}</span>
                      {sortField === 'fromLocation' && (
                        sortDirection === 'asc'
                          ? <FaSortAmountUp className="text-blue-600" />
                          : <FaSortAmountDown className="text-blue-600" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="py-3 px-4 text-left">
                    <button
                      className="group flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
                      onClick={() => handleSort('toLocation')}
                    >
                      <span>{t('To Location')}</span>
                      {sortField === 'toLocation' && (
                        sortDirection === 'asc'
                          ? <FaSortAmountUp className="text-blue-600" />
                          : <FaSortAmountDown className="text-blue-600" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="py-3 px-4 text-left">
                    <button
                      className="group flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
                      onClick={() => handleSort('carType')}
                    >
                      <span>{t('Car Type')}</span>
                      {sortField === 'carType' && (
                        sortDirection === 'asc'
                          ? <FaSortAmountUp className="text-blue-600" />
                          : <FaSortAmountDown className="text-blue-600" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="py-3 px-4 text-left">
                    <button
                      className="group flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
                      onClick={() => handleSort('lowSeasonPrice')}
                    >
                      <span>{t('Low Season')}</span>
                      {sortField === 'lowSeasonPrice' && (
                        sortDirection === 'asc'
                          ? <FaSortAmountUp className="text-blue-600" />
                          : <FaSortAmountDown className="text-blue-600" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="py-3 px-4 text-left">
                    <button
                      className="group flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
                      onClick={() => handleSort('mediumSeasonPrice')}
                    >
                      <span>{t('Medium Season')}</span>
                      {sortField === 'mediumSeasonPrice' && (
                        sortDirection === 'asc'
                          ? <FaSortAmountUp className="text-blue-600" />
                          : <FaSortAmountDown className="text-blue-600" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="py-3 px-4 text-left">
                    <button
                      className="group flex items-center space-x-1 text-xs font-medium text-gray-500 uppercase tracking-wider"
                      onClick={() => handleSort('highSeasonPrice')}
                    >
                      <span>{t('High Season')}</span>
                      {sortField === 'highSeasonPrice' && (
                        sortDirection === 'asc'
                          ? <FaSortAmountUp className="text-blue-600" />
                          : <FaSortAmountDown className="text-blue-600" />
                      )}
                    </button>
                  </th>
                  <th scope="col" className="py-3 px-4 text-right">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Actions')}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedPricings.map(pricing => (
                  editingPricing && editingPricing._id === pricing._id ? (
                    <motion.tr
                      key={`editing-${pricing._id}`}
                      initial={{ backgroundColor: "#fff9c2" }}
                      animate={{ backgroundColor: "#fffdf7" }}
                      transition={{ duration: 2, repeat: Infinity, repeatType: "reverse" }}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                            checked={selectedPricings.includes(pricing._id)}
                            onChange={() => handleSelectPricing(pricing._id)}
                          />
                        </div>
                      </td>
                      <td className="py-2 px-4">
                        <input
                          type="text"
                          name="fromLocation"
                          value={editingPricing.fromLocation}
                          onChange={handleEditChange}
                          className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </td>
                      <td className="py-2 px-4">
                        <input
                          type="text"
                          name="toLocation"
                          value={editingPricing.toLocation}
                          onChange={handleEditChange}
                          className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </td>
                      <td className="py-2 px-4">
                        <select
                          name="carType"
                          value={editingPricing.carType || 'car'}
                          onChange={handleEditChange}
                          className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          {carTypes.map(type => (
                            <option key={type} value={type}>{t(type.charAt(0).toUpperCase() + type.slice(1))}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 px-4">
                        <input
                          type="number"
                          name="lowSeasonPrice"
                          value={editingPricing.lowSeasonPrice}
                          onChange={handleEditChange}
                          min="0"
                          step="0.01"
                          className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </td>
                      <td className="py-2 px-4">
                        <input
                          type="number"
                          name="mediumSeasonPrice"
                          value={editingPricing.mediumSeasonPrice}
                          onChange={handleEditChange}
                          min="0"
                          step="0.01"
                          className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </td>
                      <td className="py-2 px-4">
                        <input
                          type="number"
                          name="highSeasonPrice"
                          value={editingPricing.highSeasonPrice}
                          onChange={handleEditChange}
                          min="0"
                          step="0.01"
                          className="w-full p-1 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </td>
                      <td className="py-2 px-4 text-right text-sm">
                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={handleEditSave}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title={t('Save')}
                          >
                            <FaSave className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingPricing(null)}
                            className="text-gray-600 hover:text-gray-900 p-1"
                            title={t('Cancel')}
                          >
                            <FaTimes className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ) : (
                    <motion.tr
                      key={pricing._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                      className={`hover:bg-gray-50 transition-colors duration-150 
                        ${selectedPricings.includes(pricing._id) ? 'bg-blue-50 shadow-sm' : ''}
                      `}
                      whileHover={{
                        scale: 1.005,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                      }}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                            checked={selectedPricings.includes(pricing._id)}
                            onChange={() => handleSelectPricing(pricing._id)}
                          />
                        </div>
                      </td>
                      <td className="py-4 px-4">{pricing.fromLocation}</td>
                      <td className="py-4 px-4">{pricing.toLocation}</td>
                      <td className="py-4 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {t(pricing.carType?.charAt(0)?.toUpperCase() + pricing.carType?.slice(1) || 'Car')}
                        </span>
                      </td>
                      <td className="py-4 px-4">{pricing.lowSeasonPrice}</td>
                      <td className="py-4 px-4">{pricing.mediumSeasonPrice}</td>
                      <td className="py-4 px-4">{pricing.highSeasonPrice}</td>
                      <td className="py-4 px-4 text-right text-sm">
                        <div className="flex justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => handleEditStart(pricing)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title={t('Edit')}
                          >
                            <FaEdit className="w-5 h-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(pricing._id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title={t('Delete')}
                            disabled={isDeleting}
                          >
                            <FaTrash className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  )
                ))}

              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination controls */}
        {pricings.length > 0 && (
          <div className="mt-4 flex flex-col space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs sm:text-sm text-gray-500">
              {t('Items per page')}:
            </span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="border border-gray-300 rounded-md p-1 text-xs sm:text-sm"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="text-xs sm:text-sm text-gray-500">
            {t('Showing')} {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredAndSortedPricings.length)} {t('of')} {filteredAndSortedPricings.length}
          </div>
            </div>

            <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md ${
              currentPage === 1
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            {t('First')}
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md ${
              currentPage === 1
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            {t('Previous')}
          </button>

          {/* Page numbers - hide on very small screens */}
          <div className="hidden xs:flex gap-1 sm:gap-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
            pageNum = i + 1;
              } else if (currentPage <= 3) {
            pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
              } else {
            pageNum = currentPage - 2 + i;
              }

              return (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`w-8 sm:w-9 py-1 text-xs sm:text-sm rounded-md ${
                currentPage === pageNum
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              {pageNum}
            </button>
              );
            })}
          </div>

          {/* Current page indicator for very small screens */}
          <div className="xs:hidden flex items-center px-2 py-1 text-xs bg-gray-100 rounded-md">
            <span>{currentPage}/{totalPages}</span>
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md ${
              currentPage === totalPages
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            {t('Next')}
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-md ${
              currentPage === totalPages
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
            }`}
          >
            {t('Last')}
          </button>
            </div>
          </div>
        )}

        {/* Keyboard shortcuts help */}
      <div className="mt-6 text-xs text-gray-400">
        <p>{t('Keyboard shortcuts')}: <kbd className="px-1 py-0.5 bg-gray-400 border border-gray-300 rounded">Ctrl+F</kbd> {t('to search')}, <kbd className="px-1 py-0.5 bg-gray-400 border border-gray-300 rounded">Ctrl+N</kbd> {t('to create new')}, <kbd className="px-1 py-0.5 bg-gray-400 border border-gray-300 rounded">Esc</kbd> {t('to cancel')}</p>
      </div>
      <ToastContainer />
    </div>
  );
};

export default TransferePage;
